// Combining the "as organizer" and "as attendee" slices of a user's insights.
//
// The nightly job already writes a combined "all" bucket for strategicGoals,
// effectiveness, structuredFeedbacks and tags. It does NOT for meetingCost or
// meetingsDistribution, and the rating trend endpoint only ever returns the two
// roles. Those three are combined here instead of in a new backend job.
//
// This is safe because the two sets are disjoint: costCalculator.js's
// filterMeetingsAsAttendee drops any meeting the user organized, so
// organizer + attendee is exactly the user's calendar, with no overlap.

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isPresent = (value) => value !== null && value !== undefined;

// Listed explicitly rather than derived from the object, so a stray
// DataUpdationTimestamp or an *Ids array can never be summed.
const COST_FIELDS = [
  "totalMeetingsCost",
  "externalMeetingsCost",
  "internalMeetingsCost",
  "externalMeetingHours",
  "internalMeetingHours",
  "noOfExternalMeetings",
  "noOfInternalMeetings",
];

/**
 * Sums the meetingCost slices of one or more perspectives.
 * Returns null when every slice is missing, so the cards fall through to their
 * EmptyCard branch instead of rendering a confident $0.
 */
export const mergeMeetingCost = (slices = []) => {
  const present = slices.filter(isPresent);
  if (present.length === 0) return null;

  const merged = {};
  COST_FIELDS.forEach((field) => {
    merged[field] = present.reduce(
      (total, slice) => total + toNumber(slice?.[field]),
      0
    );
  });
  return merged;
};

/**
 * Total meeting count for a cost slice. HoursCard and AsAttendee both used to
 * add the two fields raw, which yields NaN whenever the slice is missing — and
 * NaN passes a `typeof === "number"` check, so it reached the UI as text.
 */
export const sumMeetingCount = (slice) =>
  toNumber(slice?.noOfInternalMeetings) + toNumber(slice?.noOfExternalMeetings);

/**
 * Merges the internal/external leaf of a distribution bucket.
 * Leaf shape: { totalMeetings, totalDuration, aboveAverageRating,
 *               averageRating, belowAverageRating, ratings }
 *
 * meetingsDistribution.js stores the raw per-meeting `ratings` array and
 * roundNumericValues passes arrays through untouched, so pooling them gives the
 * exact combined average. A mean of the two averages would be wrong whenever
 * the roles have different rating counts.
 */
export const mergeDistributionLeaf = (leaves = []) => {
  const present = leaves.filter(isPresent);
  if (present.length === 0) return null;

  const merged = {
    totalMeetings: present.reduce((n, l) => n + toNumber(l?.totalMeetings), 0),
    totalDuration: present.reduce((n, l) => n + toNumber(l?.totalDuration), 0),
    averageRating: 0,
    aboveAverageRating: 0,
    belowAverageRating: 0,
    ratings: [],
  };

  const hasRatingArrays = present.every((leaf) => Array.isArray(leaf?.ratings));

  if (hasRatingArrays) {
    const pooled = present.flatMap((leaf) => leaf.ratings.map(toNumber));
    merged.ratings = pooled;

    if (pooled.length > 0) {
      const mean = pooled.reduce((sum, r) => sum + r, 0) / pooled.length;
      merged.averageRating = mean;
      merged.aboveAverageRating = pooled.filter((r) => r > mean).length;
      merged.belowAverageRating = pooled.filter((r) => r < mean).length;
    }
    return merged;
  }

  // Fallback for documents written before `ratings` existed. Approximate:
  // totalMeetings counts unrated meetings too, so it is not the true weight.
  const weighted = present.reduce(
    (acc, leaf) => {
      const weight = toNumber(leaf?.totalMeetings);
      return {
        sum: acc.sum + toNumber(leaf?.averageRating) * weight,
        weight: acc.weight + weight,
      };
    },
    { sum: 0, weight: 0 }
  );
  merged.averageRating = weighted.weight ? weighted.sum / weighted.weight : 0;
  merged.aboveAverageRating = present.reduce(
    (n, l) => n + toNumber(l?.aboveAverageRating),
    0
  );
  merged.belowAverageRating = present.reduce(
    (n, l) => n + toNumber(l?.belowAverageRating),
    0
  );
  return merged;
};

const MEETING_TYPES = ["internal", "external"];

const mergeBreakdown = (breakdowns) => {
  const present = breakdowns.filter(isPresent);
  if (present.length === 0) return null;

  const buckets = new Set();
  present.forEach((breakdown) => {
    Object.keys(breakdown).forEach((bucket) => buckets.add(bucket));
  });

  const merged = {};
  buckets.forEach((bucket) => {
    merged[bucket] = {};
    MEETING_TYPES.forEach((meetingType) => {
      const leaf = mergeDistributionLeaf(
        present.map((breakdown) => breakdown?.[bucket]?.[meetingType])
      );
      if (leaf) merged[bucket][meetingType] = leaf;
    });
  });
  return merged;
};

/**
 * Merges meetingsDistribution slices. Slice shape:
 * { byDuration, byAttendeesSize, byWeekdays }
 */
export const mergeDistribution = (slices = []) => {
  const present = slices.filter(isPresent);
  if (present.length === 0) return null;

  return {
    byDuration: mergeBreakdown(present.map((s) => s?.byDuration)),
    byAttendeesSize: mergeBreakdown(present.map((s) => s?.byAttendeesSize)),
    byWeekdays: mergeBreakdown(present.map((s) => s?.byWeekdays)),
  };
};

const RATING_BUCKET_FIELDS = [
  "totalMeetings",
  "totalAttendees",
  "totalAttendeesRated",
  "totalSumOfRatings",
];

const mergeRatingPeriod = (periods) => {
  const present = periods.filter(isPresent);
  if (present.length === 0) return {};

  const keys = new Set();
  present.forEach((period) => {
    Object.keys(period).forEach((key) => keys.add(key));
  });

  const merged = {};
  keys.forEach((key) => {
    const buckets = present.map((period) => period?.[key]).filter(isPresent);
    const bucket = {};
    RATING_BUCKET_FIELDS.forEach((field) => {
      bucket[field] = buckets.reduce((n, b) => n + toNumber(b?.[field]), 0);
    });
    // Same formula the backend uses in getIndividualRating.js.
    bucket.averageRatings = bucket.totalAttendeesRated
      ? bucket.totalSumOfRatings / bucket.totalAttendeesRated
      : 0;
    merged[key] = bucket;
  });
  return merged;
};

const RATING_PERIODS = ["7 days", "28 days", "91 days"];

/**
 * Adds an "all" series to a getIndividualRating payload.
 *
 * The payload carries totalSumOfRatings and totalAttendeesRated per day/week
 * bucket for both roles, under identical keys, so the combined average is exact
 * rather than an average of two averages.
 *
 * Note the two roles answer different questions — organizer is how others rated
 * meetings the user ran, attendee is how the user rated meetings others ran.
 * Pooled, it reads as "average rating across every meeting the user was in".
 */
export const mergeIndividualRating = (individualData) => {
  if (!individualData) return individualData;

  const all = {};
  RATING_PERIODS.forEach((period) => {
    all[period] = mergeRatingPeriod([
      individualData?.organizer?.[period],
      individualData?.attendee?.[period],
    ]);
  });
  return { ...individualData, all };
};

/**
 * Flattens meeting lists from several redis keys into one, deduped by id.
 * The organizer and attendee sets are disjoint today; the dedupe keeps the
 * drill-down and the meetings table honest if that ever changes.
 */
export const combineMeetingLists = (...lists) => {
  const seen = new Set();
  const combined = [];

  lists.forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach((meeting) => {
      if (!isPresent(meeting)) return;
      const id = meeting?.id;
      if (isPresent(id)) {
        if (seen.has(id)) return;
        seen.add(id);
      }
      combined.push(meeting);
    });
  });

  return combined;
};
